// @flow
import { useCallback, useMemo } from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import AdvancedSelectField from '@shared/AdvancedSelectField';

import type { Option } from '@core/types';
import type { Period } from '@groupClass/types';

type InterventionsPeriodFilterProps = {
  periods?: Period[] | null,
  selectedPeriodId: number | null,
  onChange: (periodId: number | null) => void,
};

const formatPeriodLabel = (period: Period, index: number, periodLabel: string): string => {
  const startDate = format(new Date(period.startDate), 'dd/MM/yyyy');
  const endDate = format(new Date(period.endDate), 'dd/MM/yyyy');
  return `${ periodLabel } ${ index + 1 }: ${ startDate } - ${ endDate }`;
};

const InterventionsPeriodFilter = (props: InterventionsPeriodFilterProps): React$Node => {
  const { periods, selectedPeriodId, onChange } = props;
  const { t } = useTranslation();

  const periodsValue = periods || [];
  const hasPeriods = periodsValue.length > 0;
  const periodLabel = t('groupClasses.groupClass.interventions.periodFilter.period');

  const findCurrentPeriodId = (periods: Period[]): number | null => {
    const now = new Date();
    const current = periods.find((period) =>
      new Date(period.startDate) <= now && now <= new Date(period.endDate),
    );
    return current ? current.id : null;
  };

  const options = useMemo((): Option[] =>
    periodsValue.map((period, index) => ({
      value: String(period.id),
      label: formatPeriodLabel(period, index, periodLabel),
    }))
  , [periodLabel, periodsValue]);

  const selectedOption = useMemo((): Option | null => {
    if (!options.length) return null;

    const effectivePeriodId
      = selectedPeriodId ?? findCurrentPeriodId(periodsValue);

    return (
      options.find((option) => option.value === String(effectivePeriodId))
      || options[0]
    );
  }, [options, periodsValue, selectedPeriodId]);

  const handleChange = useCallback((newValue: Option | Option[] | null): void => {
    const option = Array.isArray(newValue) ? newValue[0] : newValue;
    const value = option?.value;
    if (!option || typeof value !== 'string') {
      onChange(null);
      return;
    }
    onChange(Number(value));
  }, [onChange]);

  if (!hasPeriods) return null;

  return (
    <div className="interventions-period-filter__container">
      <AdvancedSelectField
        name="interventions-period-filter"
        className="interventions-period-filter"
        label={ t('groupClasses.groupClass.interventions.periodFilter.label') }
        labelProps={ { className: 'interventions-period-filter__label' } }
        options={ options }
        value={ selectedOption }
        onChange={ handleChange }
        isSearchable={ false }
        isClearable={ false }
      />
    </div>
  );
};

export default InterventionsPeriodFilter;
